import Places, { PlacesByPlaceId } from 'constants/places'
import placesTree from 'constants/placesTree'

const ELIGIBLE_DESTINATION_COUNTRY_CODES = new Set(['ID', 'FR', 'ES', 'IT', 'GB', 'US', 'TH', 'AU', 'NZ'])
const ELIGIBLE_DESTINATION_IDS = new Set([
  Places.Indonesia.id,
  Places.France.id,
  Places.Spain.id,
  Places.Italy.id,
  Places.UnitedKingdom.id,
  Places.UnitedStates.id,
  Places.Thailand.id,
  Places.Australia.id,
  Places.NewZealand.id,
])
const ELIGIBLE_DESTINATION_NAMES = new Set([
  'indonesia',
  'france',
  'spain',
  'italy',
  'uk',
  'united kingdom',
  'us',
  'usa',
  'united states',
  'united states of america',
  'thailand',
  'australia',
  'new zealand',
])

interface PlaceTreeItem {
  name: string
  canonicalName?: string
  children?: Array<PlaceTreeItem>
}

interface SearchVerticalTabsEligibilityParams {
  destinationId?: string
  destinationIds?: Array<string>
  destinationName?: string
  destinationPlaces?: Array<App.Place | null | undefined>
  rooms?: Array<App.Occupants>
}

function normaliseDestinationName(destinationName?: string): string {
  return destinationName?.trim().toLowerCase() ?? ''
}

function getCanonicalNameByPlaceName(placeItems: Array<PlaceTreeItem>): Map<string, string> {
  const canonicalNameByPlaceName = new Map<string, string>()

  function addPlace(place: PlaceTreeItem) {
    canonicalNameByPlaceName.set(normaliseDestinationName(place.name), normaliseDestinationName(place.canonicalName))
    place.children?.forEach(addPlace)
  }

  placeItems.forEach(addPlace)

  return canonicalNameByPlaceName
}

const canonicalNameByPlaceName = getCanonicalNameByPlaceName(placesTree)

function destinationTextMatchesEligibleDestination(destinationText?: string): boolean {
  const destinationName = normaliseDestinationName(destinationText)

  if (!destinationName) {
    return false
  }

  const canonicalDestinationName = canonicalNameByPlaceName.get(destinationName)
  const destinationNames = canonicalDestinationName ? [destinationName, canonicalDestinationName] : [destinationName]

  return destinationNames.some(name => (
    name.split(',').some(destinationNamePart => ELIGIBLE_DESTINATION_NAMES.has(destinationNamePart.trim()))
  ))
}

export function getSearchVerticalTabsDestinationIds(params: Pick<SearchVerticalTabsEligibilityParams, 'destinationId' | 'destinationIds'>): Array<string> {
  if (params.destinationIds?.length) {
    return params.destinationIds
  }

  return params.destinationId?.split(',').filter(Boolean) ?? []
}

function isEligiblePlaceDestination(place?: App.Place | null): boolean {
  return (
    !!place?.countryCode && ELIGIBLE_DESTINATION_COUNTRY_CODES.has(place.countryCode)
  ) || destinationTextMatchesEligibleDestination(place?.canonicalName ?? place?.name)
}

export function hasSearchVerticalTabsEligibleOccupancy(rooms: Array<App.Occupants> = []): boolean {
  const totalAdults = rooms.reduce((total, room) => total + room.adults, 0)
  const hasChildren = rooms.some(room => (room.children ?? 0) > 0 || (room.childrenAge?.length ?? 0) > 0)

  return totalAdults > 2 || hasChildren
}

export function hasSearchVerticalTabsEligibleDestination(params: SearchVerticalTabsEligibilityParams): boolean {
  const destinationIds = getSearchVerticalTabsDestinationIds(params)

  if (destinationIds.some(destinationId => {
    if (ELIGIBLE_DESTINATION_IDS.has(destinationId)) {
      return true
    }

    return isEligiblePlaceDestination(PlacesByPlaceId[destinationId])
  })) {
    return true
  }

  if (params.destinationPlaces?.some(isEligiblePlaceDestination)) {
    return true
  }

  return destinationTextMatchesEligibleDestination(params.destinationName)
}

export function isSearchVerticalTabsEligible(params: SearchVerticalTabsEligibilityParams): boolean {
  return hasSearchVerticalTabsEligibleOccupancy(params.rooms) && hasSearchVerticalTabsEligibleDestination(params)
}
